import { z } from 'zod';

import { defineRoute } from '../http/route-contract';

/**
 * Body of POST /telegram/login/start. Telegram needs the account's phone number to send a code.
 * How the Mini App obtains it is open (Q70), so this shape is provisional.
 */
export const StartTelegramLoginRequestSchema = z.strictObject({
  phoneNumber: z
    .string()
    .regex(/^\+[1-9]\d{6,14}$/, 'Expected an E.164 phone number')
    .describe('E.164 number of the Telegram account. Never echoed or logged (I9).'),
});
/** POST /telegram/login/start body. */
export type StartTelegramLoginRequest = z.infer<typeof StartTelegramLoginRequestSchema>;

/** Body of POST /telegram/login/code. */
export const SubmitTelegramCodeRequestSchema = z.strictObject({
  code: z
    .string()
    .regex(/^\d{1,10}$/)
    .describe('Code received in Telegram. Never echoed or logged (I9).'),
});
/** POST /telegram/login/code body. */
export type SubmitTelegramCodeRequest = z.infer<typeof SubmitTelegramCodeRequestSchema>;

/** Body of POST /telegram/login/password. */
export const SubmitTelegramPasswordRequestSchema = z.strictObject({
  password: z
    .string()
    .min(1)
    .max(256)
    .describe(
      'Telegram two-step verification password. Forwarded once; never stored, echoed or logged (I9, FXM-01 §7).',
    ),
});
/** POST /telegram/login/password body. */
export type SubmitTelegramPasswordRequest = z.infer<typeof SubmitTelegramPasswordRequestSchema>;

/**
 * Next step of the Telegram login (FXM-01 §5). "Password required" is a step, not an error
 * (C1 spec §5.1). Failures are Problems with a reason code.
 */
export const TelegramLoginStepSchema = z.discriminatedUnion('step', [
  z.object({
    step: z.literal('CODE_SENT'),
    // S4 R2: the code screen names the account the code went to, masked. Never the full number (I9).
    phoneMasked: z
      .string()
      .min(1)
      .max(32)
      .describe('Masked number, e.g. "+33 6 •• •• •• 42". Provisional (S4, Q70).'),
    codeLength: z.int().min(1).max(10).describe('Number of digits of the code Telegram sent.'),
  }),
  z.object({ step: z.literal('PASSWORD_REQUIRED') }),
  z.object({ step: z.literal('LOGGED_IN') }),
]);
/** Login step. */
export type TelegramLoginStep = z.infer<typeof TelegramLoginStepSchema>;

const loginStep = { name: 'TelegramLoginStep', schema: TelegramLoginStepSchema } as const;
const SOURCE = 'technical spec §6; FXM-01 §4, §5; US-03';

/** POST /telegram/login/start. */
export const startTelegramLoginRoute = defineRoute({
  operationId: 'startTelegramLogin',
  method: 'POST',
  path: '/telegram/login/start',
  tag: 'Telegram login',
  summary: 'Ask Telegram to send a login code',
  description: 'Starts the Telegram login for the trader; Telegram sends a code in the app.',
  source: SOURCE,
  auth: 'bearer',
  provisional: {
    questions: ['Q70', 'Q33'],
    note: 'phone number source and rate-limit message are open.',
  },
  body: { name: 'StartTelegramLoginRequest', schema: StartTelegramLoginRequestSchema },
  success: { kind: 'json', status: 200, body: loginStep },
  errors: [
    { status: 422, reasonCode: 'TELEGRAM_PHONE_NUMBER_INVALID' },
    { status: 429, reasonCode: 'TELEGRAM_CODE_RATE_LIMITED' },
    { status: 503, reasonCode: 'TELEGRAM_UNAVAILABLE' },
    // S4 R13: a retry that arrives while the first request still runs.
    { status: 409, reasonCode: 'IDEMPOTENCY_KEY_IN_PROGRESS' },
  ],
});

/** POST /telegram/login/code. */
export const submitTelegramCodeRoute = defineRoute({
  operationId: 'submitTelegramCode',
  method: 'POST',
  path: '/telegram/login/code',
  tag: 'Telegram login',
  summary: 'Submit the Telegram login code',
  description:
    'Answers LOGGED_IN, or PASSWORD_REQUIRED when the account has two-step verification.',
  source: SOURCE,
  auth: 'bearer',
  provisional: {
    questions: ['Q33', 'Q76'],
    note: 'rate-limit message and delay, and the other-account rule, are open.',
  },
  body: { name: 'SubmitTelegramCodeRequest', schema: SubmitTelegramCodeRequestSchema },
  success: { kind: 'json', status: 200, body: loginStep },
  errors: [
    { status: 422, reasonCode: 'TELEGRAM_CODE_INVALID' },
    { status: 422, reasonCode: 'TELEGRAM_CODE_EXPIRED' },
    { status: 409, reasonCode: 'TELEGRAM_LOGIN_NOT_STARTED' },
    { status: 409, reasonCode: 'TELEGRAM_ACCOUNT_MISMATCH' },
    { status: 429, reasonCode: 'RATE_LIMITED' },
    { status: 429, reasonCode: 'TELEGRAM_CODE_RATE_LIMITED' },
    { status: 503, reasonCode: 'TELEGRAM_UNAVAILABLE' },
    // S4 R13: a retry that arrives while the first request still runs.
    { status: 409, reasonCode: 'IDEMPOTENCY_KEY_IN_PROGRESS' },
  ],
});

/** POST /telegram/login/password. */
export const submitTelegramPasswordRoute = defineRoute({
  operationId: 'submitTelegramPassword',
  method: 'POST',
  path: '/telegram/login/password',
  tag: 'Telegram login',
  summary: 'Submit the Telegram two-step verification password',
  description: 'Completes the login when Telegram asked for the 2FA password.',
  source: SOURCE,
  auth: 'bearer',
  provisional: {
    questions: ['Q33', 'Q76'],
    note: 'rate-limit message and delay, and the other-account rule, are open.',
  },
  body: { name: 'SubmitTelegramPasswordRequest', schema: SubmitTelegramPasswordRequestSchema },
  success: { kind: 'json', status: 200, body: loginStep },
  errors: [
    { status: 422, reasonCode: 'TELEGRAM_PASSWORD_INVALID' },
    { status: 409, reasonCode: 'TELEGRAM_LOGIN_NOT_STARTED' },
    { status: 409, reasonCode: 'TELEGRAM_ACCOUNT_MISMATCH' },
    { status: 429, reasonCode: 'RATE_LIMITED' },
    { status: 429, reasonCode: 'TELEGRAM_CODE_RATE_LIMITED' },
    { status: 503, reasonCode: 'TELEGRAM_UNAVAILABLE' },
    // S4 R13: a retry that arrives while the first request still runs.
    { status: 409, reasonCode: 'IDEMPOTENCY_KEY_IN_PROGRESS' },
  ],
});

/** POST /telegram/logout. */
export const logoutTelegramRoute = defineRoute({
  operationId: 'logoutTelegram',
  method: 'POST',
  path: '/telegram/logout',
  tag: 'Telegram login',
  summary: 'Log out of Telegram and stop all copying',
  description: 'Revokes the Telegram session; all copying stops immediately (FXM-01 §4).',
  source: 'technical spec §6; FXM-01 §4; US-08',
  auth: 'bearer',
  provisional: { questions: ['Q31'], note: 'what happens to open positions is open.' },
  success: { kind: 'empty', status: 204 },
  // C1 spec §5.1 row 3, AC20: revoking the session needs Telegram.
  errors: [{ status: 503, reasonCode: 'TELEGRAM_UNAVAILABLE' }],
});
